const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const entryFields = (value) => value?.fields || value || {};

const playerDetails = (reference) => {
  const fields = entryFields(reference);
  return {
    id:
      reference?.sys?.id ||
      fields?.sys?.id ||
      [fields.firstName, fields.lastName].filter(Boolean).join("-") ||
      fields.name ||
      "unknown-player",
    name:
      [fields.firstName, fields.lastName].filter(Boolean).join(" ") ||
      fields.name ||
      "Sporting Lagos player",
    number: fields.playerNumber,
    position: fields.position,
  };
};

export const getSeasonLabel = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const startYear = parsed.getMonth() >= 6 ? year : year - 1;
  return `${startYear}/${String(startYear + 1).slice(-2)}`;
};

export const getMatchSeason = (match) => {
  const fields = entryFields(match);
  return fields.season || getSeasonLabel(fields.matchDate);
};

export const getGoalEvents = (match) => {
  const fields = entryFields(match);
  const events =
    fields.goalEvents || fields.goals || fields.matchDetails?.goals || [];

  return (Array.isArray(events) ? events : [])
    .map(entryFields)
    .filter((event) => event && Object.keys(event).length);
};

export const getMatchScores = (match) => {
  const fields = entryFields(match);
  return {
    sporting: asNumber(
      fields.sportingScore ?? fields.matchDetails?.scores?.sporting
    ),
    opponent: asNumber(
      fields.opponentScore ?? fields.matchDetails?.scores?.opponent
    ),
  };
};

const matchCompetition = (match) => {
  const fields = entryFields(match);
  return (
    fields.competition ||
    fields.leagueAbbreviation ||
    fields.leagueFullName ||
    "Other"
  );
};

const addPlayerTotal = (table, reference, stat) => {
  if (!reference) return;
  const player = playerDetails(reference);
  if (!table[player.id]) table[player.id] = { ...player, goals: 0, assists: 0 };
  table[player.id][stat] += 1;
};

const rankedPlayers = (table, stat) =>
  Object.values(table)
    .filter((player) => player[stat] > 0)
    .sort((a, b) => b[stat] - a[stat] || a.name.localeCompare(b.name));

export const buildStatistics = (
  matches,
  { team = "first", season = "", competition = "all" } = {}
) => {
  const selected = (matches || []).filter((match) => {
    const fields = entryFields(match);
    return (
      fields.played &&
      (!team || fields.team === team) &&
      (!season || getMatchSeason(match) === season) &&
      (competition === "all" || matchCompetition(match) === competition)
    );
  });

  const totals = {
    played: selected.length,
    wins: 0,
    draws: 0,
    losses: 0,
    points: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    cleanSheets: 0,
  };
  const players = {};

  selected.forEach((match) => {
    const { sporting, opponent } = getMatchScores(match);

    totals.goalsFor += sporting;
    totals.goalsAgainst += opponent;
    if (opponent === 0) totals.cleanSheets += 1;

    if (sporting > opponent) {
      totals.wins += 1;
      totals.points += 3;
    } else if (sporting === opponent) {
      totals.draws += 1;
      totals.points += 1;
    } else {
      totals.losses += 1;
    }

    getGoalEvents(match).forEach((event) => {
      addPlayerTotal(players, event.scorer, "goals");
      addPlayerTotal(players, event.assister || event.assist, "assists");
    });
  });

  return {
    ...totals,
    goalDifference: totals.goalsFor - totals.goalsAgainst,
    winRate: totals.played
      ? Math.round((totals.wins / totals.played) * 100)
      : 0,
    goalsPerGame: totals.played
      ? (totals.goalsFor / totals.played).toFixed(1)
      : "0.0",
    goalsAgainstPerGame: totals.played
      ? (totals.goalsAgainst / totals.played).toFixed(1)
      : "0.0",
    topScorers: rankedPlayers(players, "goals"),
    topAssisters: rankedPlayers(players, "assists"),
  };
};

export const getAvailableSeasons = (matches, team = "first") =>
  [...new Set(
    (matches || [])
      .filter((match) => !team || entryFields(match).team === team)
      .map(getMatchSeason)
      .filter(Boolean)
  )].sort().reverse();

export const getCurrentSeason = (matches, team = "first", date = new Date()) => {
  const seasons = getAvailableSeasons(matches, team);
  const calendarSeason = getSeasonLabel(date);

  return seasons.includes(calendarSeason) ? calendarSeason : seasons[0] || calendarSeason;
};

export const getPlayerSeasonStats = (
  matches,
  playerId,
  { team = "first", season = "", competition = "all" } = {}
) => {
  if (!playerId) return { goals: 0, assists: 0 };

  const statistics = buildStatistics(matches, { team, season, competition });
  const scorer = statistics.topScorers.find((player) => player.id === playerId);
  const assister = statistics.topAssisters.find((player) => player.id === playerId);

  return {
    goals: scorer?.goals || 0,
    assists: assister?.assists || 0,
  };
};

export const getAvailableCompetitions = (matches, team = "first", season = "") =>
  [...new Set(
    (matches || [])
      .filter((match) => {
        const fields = entryFields(match);
        return (
          (!team || fields.team === team) &&
          (!season || getMatchSeason(match) === season)
        );
      })
      .map(matchCompetition)
      .filter(Boolean)
  )].sort();
